import { Controller } from '@hotwired/stimulus';

const CIRCUMFERENCE = 2 * Math.PI * 14; // matches r=14 on the SVG circle

export default class extends Controller {
  static targets = [
    'select',
    'playButton',
    'playIcon',
    'progressArc',
    'pushCheckbox',
  ];
  static values = { soundUrls: Object };

  connect() {
    this.audio = null;
    this.isPlaying = false;
    this.animationFrame = null;
    this.resetTimeout = null;
    this.updateButtonState();
    this.resetProgress();
  }

  disconnect() {
    this.stopAudio();
  }

  selectChanged() {
    this.stopAudio();
    this.updateButtonState();
  }

  pushChanged() {
    if (!this.pushCheckboxTarget.checked) {
      this.stopAudio();
    }

    this.updateButtonState();
  }

  togglePlay() {
    if (this.isPlaying) {
      this.stopAudio();
    } else {
      this.playAudio();
    }
  }

  playAudio() {
    const soundKey = this.selectTarget.value;
    const url = this.soundUrlsValue[soundKey];
    if (!url) return;

    clearTimeout(this.resetTimeout);
    this.audio = new Audio(url);
    this.audio.addEventListener('ended', () => this.onEnded());
    this.audio.addEventListener('error', () => this.stopAudio());
    this.audio.play().catch(() => this.stopAudio());

    this.isPlaying = true;
    this.updateIcon();
    this.startProgressLoop();
  }

  onEnded() {
    this.cancelProgressLoop();
    this.setProgress(1);

    // Brief pause at 100% so the completed ring is visible before resetting
    this.resetTimeout = setTimeout(() => {
      if (this.audio) {
        this.audio = null;
      }
      this.isPlaying = false;
      this.updateIcon();
      this.resetProgress();
    }, 300);
  }

  stopAudio() {
    clearTimeout(this.resetTimeout);
    this.cancelProgressLoop();
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
    this.isPlaying = false;
    this.updateIcon();
    this.resetProgress();
  }

  startProgressLoop() {
    const tick = () => {
      this.updateProgress();
      this.animationFrame = requestAnimationFrame(tick);
    };
    this.animationFrame = requestAnimationFrame(tick);
  }

  cancelProgressLoop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  updateButtonState() {
    const pushEnabled =
      !this.hasPushCheckboxTarget || this.pushCheckboxTarget.checked;
    const hasSelection = this.selectTarget.value !== '';
    this.playButtonTarget.disabled = !pushEnabled || !hasSelection;
  }

  updateIcon() {
    if (this.isPlaying) {
      this.playIconTarget.classList.replace('fa-play', 'fa-stop');
    } else {
      this.playIconTarget.classList.replace('fa-stop', 'fa-play');
    }
  }

  updateProgress() {
    if (!this.audio || !this.hasProgressArcTarget || !this.audio.duration)
      return;
    const progress = this.audio.currentTime / this.audio.duration;
    this.setProgress(progress);
  }

  setProgress(progress) {
    if (!this.hasProgressArcTarget) return;
    this.progressArcTarget.style.strokeDashoffset =
      CIRCUMFERENCE * (1 - progress);
  }

  resetProgress() {
    if (!this.hasProgressArcTarget) return;
    this.progressArcTarget.style.strokeDashoffset = CIRCUMFERENCE;
  }
}
